import {
	DropdownMenuPortal,
	Root as DropdownMenuRoot,
	DropdownMenuTrigger
} from '@radix-ui/react-dropdown-menu'
import { useQuery } from '@tanstack/react-query'
import { generatePath, useNavigate } from 'react-router'
import { Button } from '@/components/ui/button'
import {
	DropdownContent,
	DropdownItem,
	DropdownSeparator
} from '@/components/ui/dropdown-atoms'
import {
	DotsHorizontalIcon,
	Edit02Icon,
	Trash01Icon
} from '@/components/ui/icons'
import { DeleteEventDialog } from '@/features/marquee/components/delete-event-dialog'
import { ResponsiveConfirmTrigger } from '@/features/marquee/components/responsive-confirm-dialog'
import { operatorQueryOptions } from '@/features/marquee/query-options'
import { isMarketManagerAllowed } from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import { routes } from '@/routes'
import type { MarqueeEventListItem } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/**
 * The shared `DropdownItem` keeps its own type, padding and hover; all this adds
 * is the row shape the design asks for: label left, icon hard right.
 */
const ITEM_CLASS = 'flex items-center justify-between gap-4'

interface EventRowMenuProps {
	event: MarqueeEventListItem
}

/**
 * The tracker row's kebab: edit and delete for the event on that card.
 *
 * Renders nothing when neither item applies, rather than an empty popup, and
 * the card is then exactly what it was before this existed.
 *
 * The confirmation is mounted *around* the menu, with the delete row as its
 * trigger: the menu unmounts its content on select, so a dialog kept inside it
 * would be torn down by the very click that opens it.
 *
 * Edit navigates to the edit page instead of opening a form here: that page
 * already fetches the event, and the tracker row is missing the one field the
 * form needs (`assigned_mms`, which only the detail carries).
 */
export function EventRowMenu({ event }: EventRowMenuProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	// Setting an event up and pruning the tracker are both creator work. One
	// cached answer for the whole session, so asking it per row costs nothing.
	const { data: operator } = useQuery(operatorQueryOptions())
	const canDelete = isMarketManagerAllowed(operator, 'deleteEvent')

	// Role is only half of it for the edit: the backend takes one in `pre_work`
	// only, while a delete is accepted in every state, the closed and
	// already-exported ones included.
	const showEdit =
		isMarketManagerAllowed(operator, 'updateEvent') &&
		event.state === 'pre_work'

	if (!(showEdit || canDelete)) {
		return null
	}

	const menu = (
		<DropdownMenuRoot>
			<DropdownMenuTrigger asChild>
				{/* `square` + `md` is 40px around a 20px glyph, which leaves
				    exactly the 10px the menu's rows carry: that is what puts
				    their icons on the same vertical line as these dots. Only
				    the two backgrounds are added, the shape is the variant's. */}
				<Button
					aria-label={t('marquee:events_row_menu_label')}
					className='text-neutral-500 transition-colors hover:bg-grey-100 hover:text-neutral-700 data-[state=open]:bg-grey-200 data-[state=open]:text-neutral-700'
					onClick={(clickEvent) => {
						clickEvent.preventDefault()
						clickEvent.stopPropagation()
					}}
					shape='square'
					size='md'
					type='button'
					variant='tertiary'
				>
					<DotsHorizontalIcon size={20} />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownContent
					align='end'
					onClick={(clickEvent) => clickEvent.stopPropagation()}
					sideOffset={4}
				>
					{showEdit && (
						<DropdownItem
							className={ITEM_CLASS}
							onClick={() =>
								navigate(
									generatePath(
										routes.MARQUEE_EVENT_EDIT.handle
											.fullPath,
										{ id: event.id }
									)
								)
							}
						>
							{t('marquee:edit_event')}
							<Edit02Icon size={20} />
						</DropdownItem>
					)}
					{showEdit && canDelete && <DropdownSeparator />}
					{canDelete && (
						<ResponsiveConfirmTrigger>
							<DropdownItem
								className={cn(ITEM_CLASS, 'text-red-700')}
							>
								{t('marquee:events_delete')}
								<Trash01Icon size={20} />
							</DropdownItem>
						</ResponsiveConfirmTrigger>
					)}
				</DropdownContent>
			</DropdownMenuPortal>
		</DropdownMenuRoot>
	)

	if (!canDelete) {
		return menu
	}

	return (
		<DeleteEventDialog eventId={event.id} eventName={event.name}>
			{menu}
		</DeleteEventDialog>
	)
}
